import { DRUID_PATH } from '@somnhold/content';
import { cellAtWorld, cellId, heroWalkable } from '../grid.ts';
import { hexToWorld, ROW } from '../hex.ts';
import { dist2, iatan2, idiv, imax, imin, mulBp, withinRange } from '../math/fixed.ts';
import { rngInt, rngRange } from '../math/rng.ts';
import type { BattleState, DruidRules, DruidState, Enemy, Hero, SendaNpc } from '../types.ts';
import { damageHero, ev, findEnemy, spawnEnemy } from './combat.ts';
import { orderMove } from './heroes.ts';
import { stepToward } from './enemies.ts';
import { rootsAdvance, rootsCut, rootsFresh, rootsLose, rootsSow, rootsWon, type GladeLayout, type RootEvent, type RootNet, type StreamLayout, type ThawLayout } from './druid-layout.ts';

/**
 * The Circle's wood under the Grandsire (docs/sendas/druida.md, chapter 1, spring; the grounds in battle/druid-layout.ts).
 *
 * - The thaw (act 1, `thaw`): up the flooded wood to Olmo's clearing. The melt comes down in surges, a wall of water
 *   from the top of the slope going down (its roar and foam `warn` before); off a mound it catches whoever it finds,
 *   carries them down and drowns them (10 % a second); the mounds split it. From below, the flood rises and drowns the
 *   mounds too: there is no waiting it out. The mud slows. Each surge sends wolves to the mound ahead (they flee the
 *   water too); a patch of mud wakes slimes. Won at the clearing.
 * - Olmo's clearing (act 2, `glade`): Ceda looks for the face (the bar); Hob fences the shoots one by one; slimes come up
 *   out of the mud pools and go for the nearest shoot not fenced: eaten, the slime grows (its life and its blow). Lost
 *   if the hero, Ceda or Hob falls.
 * - The root bridge (act 3, the test, `roots`): the roots grow toward what sprouts (the rule in druid-layout.ts); a tap
 *   on a stone sends the hero to throw a seed on it, a tap on the knot to cut it (its Raíz, standing still). The Lodazal
 *   under the stream bubbles at a sprout a root still wants and eats it unless struck enough; then comes up at the bank
 *   by the hero and slaps; it throws the mud of the flood over the bank (the mounds keep it off). Slimes at the water's
 *   edge, more with every knot; wolves out of the wood. Won with both roots on the islet; lost out of seeds with
 *   nothing growing, or the hero down.
 *
 * Cost: a handful of points looked at once a tick (the hero against the mounds, a surge's line, the roots' two tips).
 * The slimes that go for the shoots are walked here, each to its shoot, without paths (the clearing is open).
 */

type St = DruidState;

/** No surge on the slope, no flood from below yet (the board's y runs negative to the top: −1 can't mean none). */
export const NONE = -0x3fffffff;

const fresh = (): St => ({
  front: NONE,
  next: 0,
  surges: 0,
  warned: 0,
  low: NONE,
  woke: 0,
  drown: -100,
  done: -1,
  fenced: 0,
  eaten: 0,
  fed: {},
  eating: {},
  pend: [],
  slime: 0,
  wolf: 0,
  seeds: 0,
  job: -1,
  held: 0,
  knots: 0,
  braids: 0,
  boss: 0,
  bmode: 0,
  bnode: -1,
  bx: 0,
  by: 0,
  bt: 0,
  bhp: 0,
  bnext: 0,
  wave: -1,
  wnext: 0,
  slowUntil: 0,
  weave: -1,
});

/** The rules from a layout (the fight's builder in campaign/sendas.ts and the drawing's scenes build them the same way). */
export const thawRules = (L: ThawLayout, R: number): DruidRules => {
  const T = DRUID_PATH.thaw;
  const id = (h: { q: number; r: number }): number => cellId(R, h.q, h.r);
  const cells: { c: number; m: number }[] = [];
  L.mounds.forEach((m, k) => m.forEach((h) => cells.push({ c: id(h), m: k })));
  cells.sort((a, b) => a.c - b.c);
  const mid = (hs: { q: number; r: number }[]): { x: number; y: number } => {
    let x = 0;
    let y = 0;
    for (const h of hs) {
      const p = hexToWorld(h.q, h.r);
      x += p.x;
      y += p.y;
    }
    return { x: idiv(x, hs.length), y: idiv(y, hs.length) };
  };
  return {
    kind: 'thaw',
    bossHp: 0,
    thaw: {
      mound: cells.map((c) => c.c),
      moundOf: cells.map((c) => c.m),
      mud: L.mud.flat().map(id).sort((a, b) => a - b),
      patches: L.mud.map(mid),
      mounds: L.mounds.map(mid),
      glade: { ...L.glade },
      top: L.top,
      foot: L.foot,
      surge: { ...T.surge },
      rise: { ...T.rise },
      wadeBp: T.wadeBp,
      drownBp: T.drownBp,
      mudBp: T.mudBp,
      wolves: T.wolves,
      wolfFrom: T.wolfFrom,
      slimes: T.slimes,
      wake: T.wake,
      reach: T.reach,
    },
  };
};

export const gladeRules = (L: GladeLayout): DruidRules => {
  const G = DRUID_PATH.glade;
  return {
    kind: 'glade',
    bossHp: 0,
    glade: {
      shoots: L.shoots.map((h) => hexToWorld(h.q, h.r)),
      pools: L.pools.map((h) => hexToWorld(h.q, h.r)),
      search: G.search,
      eat: G.eat,
      growBp: G.growBp,
      blowBp: G.blowBp,
      fedBlows: G.fedBlows,
      up: G.up,
      sprout: G.sprout,
      slime: { ...G.slime },
      wolves: { at: [...G.wolves.at], n: G.wolves.n },
    },
  };
};

export const streamRules = (L: StreamLayout, R: number, bossHp: number): DruidRules => {
  const Q = DRUID_PATH.roots;
  return {
    kind: 'roots',
    bossHp,
    roots: {
      nodes: L.net.nodes.map((h) => ({ ...h })),
      edge: L.net.edge.map((e) => [...e]),
      per: Q.per,
      see: Q.see,
      near: Q.near,
      seeds: Q.seeds,
      throw: Q.throw,
      cut: Q.cut,
      reach: Q.reach,
      weave: Q.weave,
      mound: L.mounds
        .flat()
        .map((h) => cellId(R, h.q, h.r))
        .sort((a, b) => a - b),
      edgeAt: L.edge.map((p) => ({ ...p })),
      bankRow: imax((L.net.nodes[0] as { r: number }).r, (L.net.nodes[1] as { r: number }).r),
      lodazal: { first: Q.lodazal.first, every: Q.lodazal.every, warn: Q.lodazal.warn, eat: Q.lodazal.eat, scareBp: Q.lodazal.scareBp, slapBp: Q.lodazal.slapBp, slapR: Q.lodazal.slapR, up: Q.lodazal.up, pace: Q.lodazal.pace },
      wave: { ...Q.wave },
      slimes: { ...Q.slimes },
      wolves: { ...Q.wolves },
    },
  };
};

/** A root net's nodes in sim points. */
export const nodeAt = (r: NonNullable<DruidRules['roots']>, n: number): { x: number; y: number } => {
  const h = r.nodes[n] as { q: number; r: number };
  return hexToWorld(h.q, h.r);
};

const netOf = (r: NonNullable<DruidRules['roots']>): RootNet => ({ nodes: r.nodes, edge: r.edge });

/** A nightmare of the act at a point, its life scaled as the fight's are. */
const spawn = (s: BattleState, kind: 'wolf' | 'slime', x: number, y: number): Enemy => {
  const e = spawnEnemy(s, kind, 1, false, 0, x, y);
  if (s.rules?.enemyHpBp) e.hp = e.maxHp = imax(1, mulBp(e.maxHp, s.rules.enemyHpBp));
  return e;
};

const sorted = (a: number[], v: number): boolean => {
  let lo = 0;
  let hi = a.length - 1;
  while (lo <= hi) {
    const m = (lo + hi) >> 1;
    const x = a[m] as number;
    if (x === v) return true;
    if (x < v) lo = m + 1;
    else hi = m - 1;
  }
  return false;
};

/* ---------- the thaw ---------- */

/** Whether a point is under water in the thaw: in a surge's wall (off a mound) or under the flood from below. */
export const thawWet = (s: BattleState, x: number, y: number): boolean => {
  const r = s.rules?.senda?.druid?.thaw;
  const w = s.senda?.druid;
  if (!r || !w) return false;
  if (w.low !== NONE && y > w.low) return true;
  if (w.front === NONE || y > w.front || y < w.front - r.surge.len) return false;
  return !sorted(r.mound, cellAtWorld(s, x, y));
};

/** Whether the hero stands on a mound (the thaw's, or the bank's in the test). */
export const onMound = (s: BattleState, x: number, y: number): boolean => {
  const d = s.rules?.senda?.druid;
  const c = cellAtWorld(s, x, y);
  return !!d && sorted(d.thaw?.mound ?? d.roots?.mound ?? [], c);
};

const thawTick = (s: BattleState, r: NonNullable<DruidRules['thaw']>, w: St, cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  // The surge: warned at the top, then down the slope as a wall of water.
  if (w.front === NONE && w.warned === w.surges && s.waveT >= w.next - r.surge.warn) {
    w.warned++;
    ev(s, { t: 'senda', kind: 'surgeWarn', npc: '', x: r.glade.x, y: r.top, n: r.surge.warn });
    cue(`surge${w.warned}`);
  }
  if (w.front === NONE && s.waveT >= w.next) {
    w.front = r.top;
    w.surges++;
    w.next = s.waveT + r.surge.every;
    ev(s, { t: 'senda', kind: 'surge', npc: '', x: r.glade.x, y: r.top, n: w.surges });
    // Wolves to the mound ahead of the hero: they flee the water too.
    if (h && s.waveT >= r.wolfFrom) {
      let best = -1;
      for (let k = 0; k < r.mounds.length; k++) {
        const m = r.mounds[k] as { x: number; y: number };
        if (m.y < h.y - 600 && (best < 0 || m.y > (r.mounds[best] as { y: number }).y)) best = k;
      }
      const m = best >= 0 ? (r.mounds[best] as { x: number; y: number }) : r.glade;
      for (let k = 0; k < r.wolves; k++) {
        const side = k % 2 === 0 ? -1 : 1;
        let x = m.x + side * 2600;
        const y = m.y - 600 + k * 400;
        if (!heroWalkable(s, cellAtWorld(s, x, y))) x = m.x + side * 900;
        spawn(s, 'wolf', x, y);
      }
      cue('wolves');
    }
  }
  if (w.front !== NONE) {
    w.front += r.surge.speed;
    if (w.front - r.surge.len > r.foot) w.front = NONE;
  }
  // The flood from below.
  if (s.waveT >= r.rise.first) {
    if (w.low === NONE) {
      w.low = r.foot;
      cue('rise');
      ev(s, { t: 'senda', kind: 'rise', npc: '', x: 0, y: r.foot });
    }
    w.low -= r.rise.speed;
  }
  if (!h) return;
  // In the water: carried down by a surge, drowning a tenth of the life a second.
  if (thawWet(s, h.x, h.y)) {
    if (s.tick - w.drown >= 12) {
      w.drown = s.tick;
      damageHero(s, h, imax(1, mulBp(h.stats.maxHp, r.drownBp)), null, 'physical', true);
      cue('wet');
    }
    if (w.front !== NONE && h.y <= w.front && h.y >= w.front - r.surge.len) {
      const y = h.y + r.surge.drift;
      if (heroWalkable(s, cellAtWorld(s, h.x, y))) h.y = y;
    }
  }
  // A patch of mud wakes as the hero comes near.
  for (let k = 0; k < r.patches.length && k < 30; k++) {
    if (w.woke & (1 << k)) continue;
    const p = r.patches[k] as { x: number; y: number };
    if (!withinRange(p.x, p.y, h.x, h.y, r.wake)) continue;
    w.woke |= 1 << k;
    for (let i = 0; i < r.slimes; i++) spawn(s, 'slime', p.x + (i > 0 ? 500 : -500), p.y + 300);
    ev(s, { t: 'senda', kind: 'wake', npc: '', x: p.x, y: p.y, n: k });
  }
  // Olmo's clearing.
  if (w.done < 0 && withinRange(h.x, h.y, r.glade.x, r.glade.y, r.reach)) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'glade', npc: '', x: r.glade.x, y: r.glade.y });
    cue('glade');
  }
};

/* ---------- Olmo's clearing ---------- */

const hobOf = (s: BattleState): SendaNpc | undefined => s.senda?.npcs.find((n) => n.id === 'hob');

/** How many of the clearing's shoots are up by now: `up` at the start, one more every `sprout` (Hob's order). */
export const gladeUp = (r: NonNullable<DruidRules['glade']>, waveT: number): number => imin(r.shoots.length, r.up + idiv(waveT, imax(1, r.sprout)));

const gladeTick = (s: BattleState, r: NonNullable<DruidRules['glade']>, w: St, cue: (c: string) => void): void => {
  const up = gladeUp(r, s.waveT);
  // Hob skips a shoot already eaten; those he has done are fenced. One not up yet he waits at (nothing to fence).
  const hob = hobOf(s);
  if (hob) {
    while (hob.spot < up && w.eaten & (1 << hob.spot)) {
      hob.spot++;
      hob.held = 0;
    }
    if (hob.spot >= up && hob.spot < r.shoots.length) hob.held = 0;
    let f = 0;
    for (let i = 0; i < hob.spot && i < r.shoots.length; i++) if (!(w.eaten & (1 << i))) f |= 1 << i;
    if (f !== w.fenced) {
      const n = (x: number): number => {
        let c = 0;
        for (let v = x; v; v &= v - 1) c++;
        return c;
      };
      for (let i = 0; i < r.shoots.length; i++) {
        if (f & (1 << i) && !(w.fenced & (1 << i))) {
          const p = r.shoots[i] as { x: number; y: number };
          ev(s, { t: 'senda', kind: 'fence', npc: 'hob', x: p.x, y: p.y, n: i });
        }
      }
      w.fenced = f;
      cue(`fence${n(f)}`);
    }
    // Done with the shoots: back by Ceda, at the old trunk.
    const ceda = s.senda?.npcs.find((x) => x.id === 'ceda');
    if (hob.spot >= r.shoots.length && ceda && hob.hp > 0 && !withinRange(hob.x, hob.y, ceda.x + 800, ceda.y + 400, 200)) {
      hob.moving = true;
      stepToward(hob, ceda.x + 800, ceda.y + 400, 70);
    }
  }
  // Slimes out of the mud pools, more as the search goes on.
  if (s.waveT >= w.slime) {
    w.slime = s.waveT + r.slime.every;
    const n = r.slime.n + idiv(s.waveT, r.slime.more);
    for (let k = 0; k < n; k++) {
      const p = r.pools[(w.surges + k) % r.pools.length] as { x: number; y: number };
      spawn(s, 'slime', p.x + rngRange(s.rng, -300, 300), p.y + rngRange(s.rng, -300, 300));
    }
    w.surges++;
  }
  // Wolves off the flood now and then.
  if (w.wolf < r.wolves.at.length && s.waveT >= (r.wolves.at[w.wolf] as number)) {
    const p = r.pools[rngInt(s.rng, r.pools.length)] as { x: number; y: number };
    const h = s.heroes.find((x) => x.downTicks === 0);
    for (let k = 0; k < r.wolves.n; k++) {
      const e = spawn(s, 'wolf', p.x + k * 500, p.y);
      // (They flee the water onto whoever stands in the clearing with a staff: the hero.)
      if (h) {
        e.taunter = h.id;
        e.tauntTicks = 0x3fffffff;
      }
    }
    w.wolf++;
    cue(`wolf${w.wolf}`);
  }
  if (w.done < 0 && s.waveT >= r.search) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'glade', npc: 'ceda', x: 0, y: -3 * ROW });
    cue('found');
  }
};

/**
 * A slime in the clearing goes for the nearest shoot neither fenced nor eaten, walking past the hero (true: moved
 * here); one that ate goes for Hob, who fences them, and stands at him (its blows come from senda.ts `struck`, each
 * `fedBlows` heavier for every shoot it ate: `gladeBlow`).
 */
const gladeSlime = (s: BattleState, r: NonNullable<DruidRules['glade']>, w: St, e: Enemy, px: number, cue: (c: string) => void): boolean => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  const hob = hobOf(s);
  if ((w.fed[e.id] ?? 0) > 0 && hob && hob.hp > 0) {
    delete w.eating[e.id];
    e.tgtKind = 0;
    e.tgtId = 0;
    if (withinRange(e.x, e.y, hob.x, hob.y, 450)) {
      if (hob.x !== e.x || hob.y !== e.y) e.facing = iatan2(hob.y - e.y, hob.x - e.x);
      if (e.cd === 0) {
        e.cd = e.reload;
        e.anim = 'attack';
        e.animT = 0;
      }
      return true;
    }
    e.anim = 'walk';
    stepToward(e, hob.x, hob.y, px);
    return true;
  }
  let best = -1;
  let bd = 0x7fffffff;
  // (Not the one Hob is fencing: they go for the ones left alone.)
  const busy = hobOf(s)?.spot ?? -1;
  const up = gladeUp(r, s.waveT);
  for (let i = 0; i < up; i++) {
    if ((w.fenced | w.eaten) & (1 << i) || i === busy) continue;
    const p = r.shoots[i] as { x: number; y: number };
    const d = dist2(e.x, e.y, p.x, p.y);
    if (d < bd) {
      bd = d;
      best = i;
    }
  }
  if (best < 0) {
    // Nothing left to eat: at the one with the staff.
    delete w.eating[e.id];
    if (h && e.taunter === 0) {
      e.taunter = h.id;
      e.tauntTicks = 0x3fffffff;
    }
    return false;
  }
  const p = r.shoots[best] as { x: number; y: number };
  if (!withinRange(e.x, e.y, p.x, p.y, 260)) {
    delete w.eating[e.id];
    e.anim = 'walk';
    stepToward(e, p.x, p.y, px);
    return true;
  }
  const since = w.eating[e.id];
  if (since === undefined) {
    w.eating[e.id] = s.tick;
    e.anim = 'attack';
    e.animT = 0;
    return true;
  }
  if (s.tick - since < r.eat) return true;
  // Eaten: it grows.
  delete w.eating[e.id];
  w.eaten |= 1 << best;
  const fed = (w.fed[e.id] ?? 0) + 1;
  w.fed[e.id] = fed;
  const add = mulBp(e.maxHp, r.growBp);
  e.maxHp += add;
  e.hp = imin(e.maxHp, e.hp + add);
  e.damage += mulBp(e.damage, r.blowBp);
  ev(s, { t: 'senda', kind: 'eat', npc: '', x: p.x, y: p.y, n: best });
  ev(s, { t: 'senda', kind: 'grow', npc: '', x: e.x, y: e.y, n: fed });
  let c = 0;
  for (let v = w.eaten; v; v &= v - 1) c++;
  cue(`eat${c}`);
  return true;
};

/** How many blows a nightmare by one of the clearing's people lands a second (senda.ts `struck`): a fed slime's weigh more. */
export const gladeBlow = (s: BattleState, e: Enemy): number => {
  const r = s.rules?.senda?.druid?.glade;
  const w = s.senda?.druid;
  return r && w && e.kind === 'slime' ? 1 + (w.fed[e.id] ?? 0) * r.fedBlows : 1;
};

/* ---------- the root bridge ---------- */

/** The knot's mark (the `mark` command's `at` for it). */
export const KNOT = 99;

/** The hero sent to a stone (to throw a seed) or to the knot (to cut it). */
export const druidOrder = (s: BattleState, h: Hero, at: number): void => {
  const r = s.rules?.senda?.druid?.roots;
  const w = s.senda?.druid;
  if (!r || !w?.roots || w.done >= 0) return;
  const node = at === KNOT ? w.roots.knot : at;
  if (node < 3 || node >= r.nodes.length) return;
  w.job = at === KNOT ? -2 : node;
  w.held = 0;
  const p = nodeAt(r, node);
  h.target = 0;
  h.forced = false;
  if (withinRange(h.x, h.y, p.x, p.y, r.reach - 300)) {
    orderMove(s, h, h.x, h.y);
    return;
  }
  // Down to the bank below it, as near as the bank goes.
  const y = (r.bankRow + 1) * ROW;
  orderMove(s, h, p.x, y);
};

/** The hero's job by a stone or the knot: standing still within reach, a seed thrown or the knot cut. */
const rootsWork = (s: BattleState, r: NonNullable<DruidRules['roots']>, w: St, out: RootEvent[], cue: (c: string) => void): void => {
  const h = s.heroes.find((x) => x.downTicks === 0);
  if (!h || w.job === -1 || !w.roots) return;
  const node = w.job === -2 ? w.roots.knot : w.job;
  if (node < 0) {
    w.job = -1;
    return;
  }
  const p = nodeAt(r, node);
  if (h.moving || !withinRange(h.x, h.y, p.x, p.y, r.reach)) {
    w.held = 0;
    if (!h.moving) w.job = -1;
    return;
  }
  if (++w.held < (w.job === -2 ? r.cut : r.throw)) return;
  w.held = 0;
  if (w.job === -2) {
    rootsCut(w.roots, out);
    ev(s, { t: 'senda', kind: 'knotCut', npc: '', x: p.x, y: p.y, n: node });
    cue('cut');
  } else if (w.seeds > 0 && !(w.roots.sprout & (1 << node))) {
    w.seeds--;
    rootsSow(w.roots, node);
    ev(s, { t: 'senda', kind: 'sow', npc: '', x: p.x, y: p.y, n: node });
    if (w.seeds === 3) cue('seeds3');
  }
  w.job = -1;
};

const lodazalOf = (s: BattleState, w: St): Enemy | null => {
  const e = w.boss ? findEnemy(s, w.boss) : null;
  return e && e.hp > 0 ? e : null;
};

/** Under the stream: out of reach and out of sight. */
const DEEP = { x: 0, y: -60000 };

const sink = (s: BattleState, r: NonNullable<DruidRules['roots']>, w: St, e: Enemy): void => {
  ev(s, { t: 'senda', kind: 'sink', npc: '', x: e.x, y: e.y });
  e.x = DEEP.x;
  e.y = DEEP.y;
  w.bmode = 0;
  w.bnode = -1;
  w.bnext = s.waveT + r.lodazal.every;
};

const lodazalTick = (s: BattleState, r: NonNullable<DruidRules['roots']>, w: St, cue: (c: string) => void): void => {
  const e = lodazalOf(s, w);
  if (!e || !w.roots) return;
  const L = r.lodazal;
  e.stun = 0;
  if (w.bmode === 0) {
    if (s.waveT < w.bnext) return;
    // A sprout a root still wants (not reached by both): bubbling there.
    const want: number[] = [];
    for (let n = 3; n < r.nodes.length; n++) if (w.roots.sprout & (1 << n) && !w.roots.tips.every((t) => t.trail.includes(n))) want.push(n);
    w.bhp = e.hp;
    if (want.length) {
      const n = want[rngInt(s.rng, want.length)] as number;
      const p = nodeAt(r, n);
      e.x = p.x;
      e.y = p.y + 200;
      w.bmode = 1;
      w.bnode = n;
      w.bt = s.tick + L.warn;
      ev(s, { t: 'senda', kind: 'bubble', npc: '', x: p.x, y: p.y, n });
      w.braids++;
      cue(`bubble${w.braids}`);
      return;
    }
    w.bmode = 3;
  }
  // Struck enough while at a sprout: it sinks without eating.
  if ((w.bmode === 1 || w.bmode === 2) && w.bhp - e.hp >= mulBp(e.maxHp, L.scareBp)) return sink(s, r, w, e);
  if (w.bmode === 1 && s.tick >= w.bt) {
    w.bmode = 2;
    w.bt = s.tick + L.eat;
    e.anim = 'attack';
    e.animT = 0;
    return;
  }
  if (w.bmode === 2 && s.tick >= w.bt) {
    const p = nodeAt(r, w.bnode);
    rootsLose(w.roots, w.bnode);
    ev(s, { t: 'senda', kind: 'gulp', npc: '', x: p.x, y: p.y, n: w.bnode });
    w.bmode = 3;
  }
  if (w.bmode === 3) {
    // Up at the bank by the hero: the edge point nearest it.
    const h = s.heroes.find((x) => x.downTicks === 0);
    if (!h) return;
    let best = r.edgeAt[0] as { x: number; y: number };
    let bd = 0x7fffffff;
    for (const p of r.edgeAt) {
      const d = dist2(p.x, p.y, h.x, h.y);
      if (d < bd) {
        bd = d;
        best = p;
      }
    }
    e.x = best.x;
    e.y = best.y;
    e.facing = 256;
    w.bx = h.x;
    w.by = h.y;
    w.bt = s.tick + L.warn;
    w.bmode = 5;
    ev(s, { t: 'senda', kind: 'slapWarn', npc: '', x: h.x, y: h.y, n: L.warn });
    return;
  }
  if (w.bmode === 5 && s.tick >= w.bt) {
    ev(s, { t: 'senda', kind: 'slap', npc: '', x: w.bx, y: w.by });
    e.anim = 'attack';
    e.animT = 0;
    for (const h of s.heroes) if (h.downTicks === 0 && withinRange(h.x, h.y, w.bx, w.by, L.slapR)) damageHero(s, h, mulBp(h.stats.maxHp, L.slapBp), e, 'physical', true);
    w.bmode = 4;
    w.bt = s.tick + (w.weave >= 0 ? L.up - 12 : L.up);
    return;
  }
  if (w.bmode === 4 && s.tick >= w.bt) {
    // While the bridge braids it stays up: one slap after another.
    if (w.weave >= 0) w.bmode = 3;
    else sink(s, r, w, e);
  }
};

const rootsTick = (s: BattleState, r: NonNullable<DruidRules['roots']>, w: St, cue: (c: string) => void): void => {
  const st = w.roots;
  if (!st) return;
  const out: RootEvent[] = [];
  rootsWork(s, r, w, out, cue);
  if (w.done < 0) rootsAdvance(netOf(r), { per: r.per, see: r.see, near: r.near }, st, s.tick, out);
  for (const o of out) {
    const p = nodeAt(r, o.node);
    if (o.k === 'grow') ev(s, { t: 'senda', kind: 'root', npc: String(o.root), x: p.x, y: p.y, n: o.node });
    else if (o.k === 'reach') ev(s, { t: 'senda', kind: 'reach', npc: String(o.root), x: p.x, y: p.y, n: o.node });
    else if (o.k === 'home') ev(s, { t: 'senda', kind: 'home', npc: String(o.root), x: p.x, y: p.y, n: o.node });
    else if (o.k === 'braid') {
      ev(s, { t: 'senda', kind: 'braid', npc: String(o.root), x: p.x, y: p.y, n: o.node });
      cue('braid');
    } else if (o.k === 'tangle') {
      w.knots++;
      ev(s, { t: 'senda', kind: 'tangle', npc: '', x: p.x, y: p.y, n: o.node });
      cue(`tangle${w.knots}`);
      // The slimes take their chance.
      for (let k = 0; k < r.slimes.knot; k++) {
        const q = r.edgeAt[rngInt(s.rng, r.edgeAt.length)] as { x: number; y: number };
        spawn(s, 'slime', q.x, q.y);
      }
    }
  }
  // Both roots on the islet: the bridge braids; the Lodazal comes up for good. Braided (or the Lodazal down): won.
  if (w.weave < 0 && rootsWon(st)) {
    w.weave = s.tick;
    w.job = -1;
    ev(s, { t: 'senda', kind: 'braid', npc: '', x: nodeAt(r, 2).x, y: nodeAt(r, 2).y, n: r.weave });
    cue('weave');
    if (lodazalOf(s, w) && w.bmode !== 5) w.bmode = 3;
  }
  if (w.weave >= 0 && w.done < 0 && (s.tick >= w.weave + r.weave || !lodazalOf(s, w))) {
    w.done = s.tick;
    ev(s, { t: 'senda', kind: 'bridge', npc: '', x: nodeAt(r, 2).x, y: nodeAt(r, 2).y });
    cue('bridge');
    const e = lodazalOf(s, w);
    if (e) sink(s, r, w, e);
  }
  if (w.done >= 0) return;
  lodazalTick(s, r, w, cue);
  // The mud of the flood over the bank, while the Lodazal lives (and isn't up on the bank for good).
  if (lodazalOf(s, w) && w.weave < 0) {
    if (w.wave < 0 && s.waveT >= w.wnext) {
      w.wave = s.tick + r.wave.warn;
      ev(s, { t: 'senda', kind: 'waveWarn', npc: '', x: 0, y: r.bankRow * ROW, n: r.wave.warn });
      w.surges++;
      cue(`wave${w.surges}`);
    } else if (w.wave >= 0 && s.tick >= w.wave) {
      w.wave = -1;
      w.wnext = s.waveT + r.wave.every;
      ev(s, { t: 'senda', kind: 'mudWave', npc: '', x: 0, y: r.bankRow * ROW });
      for (const h of s.heroes) {
        if (h.downTicks > 0 || onMound(s, h.x, h.y)) continue;
        damageHero(s, h, mulBp(h.stats.maxHp, r.wave.heroBp), null, 'physical', true);
        w.slowUntil = s.tick + r.wave.slow;
      }
    }
  }
  // Slimes at the water's edge; wolves out of the wood.
  if (s.waveT >= w.slime) {
    w.slime = s.waveT + r.slimes.every;
    for (let k = 0; k < r.slimes.n; k++) {
      const q = r.edgeAt[rngInt(s.rng, r.edgeAt.length)] as { x: number; y: number };
      spawn(s, 'slime', q.x, q.y);
    }
  }
  if (s.waveT >= w.wolf) {
    w.wolf = s.waveT + r.wolves.every;
    const side = rngInt(s.rng, 2) === 0 ? -1 : 1;
    for (let k = 0; k < r.wolves.n; k++) spawn(s, 'wolf', side * 7000, (r.bankRow + 2 + k) * ROW);
  }
};

/* ---------- the act ---------- */

export const druidTick = (s: BattleState, cue: (c: string) => void): void => {
  const d = s.rules?.senda?.druid;
  const st = s.senda;
  if (!d || !st) return;
  if (!st.druid) {
    const w = fresh();
    st.druid = w;
    if (d.thaw) w.next = d.thaw.surge.first;
    if (d.glade) w.slime = d.glade.slime.first;
    if (d.roots) {
      const R = d.roots;
      w.roots = rootsFresh(netOf(R));
      w.seeds = R.seeds;
      w.bnext = R.lodazal.first;
      w.wnext = R.wave.first;
      w.slime = R.slimes.first;
      w.wolf = R.wolves.first;
      const e = spawnEnemy(s, 'drowned', 1, false, 0, DEEP.x, DEEP.y, d.bossHp);
      e.speed = R.lodazal.pace;
      w.boss = e.id;
      s.bossId = e.id;
    }
  }
  const w = st.druid;
  if (!s.started) return;
  // The lines the slimes cued last tick (the enemies move after the path's tick).
  if (w.pend.length) {
    for (const c of w.pend) cue(c);
    w.pend.length = 0;
  }
  if (d.thaw) thawTick(s, d.thaw, w, cue);
  else if (d.glade) gladeTick(s, d.glade, w, cue);
  else if (d.roots) rootsTick(s, d.roots, w, cue);
};

/** Moved by the act's own rule (true): the clearing's slimes on their way to a shoot; the Lodazal always. */
export const druidHold = (s: BattleState, e: Enemy, px: (e: Enemy) => number): boolean => {
  const d = s.rules?.senda?.druid;
  const w = s.senda?.druid;
  if (!d || !w) return false;
  if (d.roots && e.id === w.boss) {
    if (e.anim !== 'attack' || e.animT > 8) e.anim = 'idle';
    return true;
  }
  if (d.glade && e.kind === 'slime') return gladeSlime(s, d.glade, w, e, px(e), (c) => w.pend.push(c));
  return false;
};

/** The hero's pace in the wood: wading, in the mud, slowed by the Lodazal's mud. */
export const druidSpeedBp = (s: BattleState, h: Hero): number => {
  const d = s.rules?.senda?.druid;
  const w = s.senda?.druid;
  if (!d || !w) return 10000;
  if (d.thaw) {
    if (thawWet(s, h.x, h.y)) return d.thaw.wadeBp;
    if (sorted(d.thaw.mud, cellAtWorld(s, h.x, h.y))) return d.thaw.mudBp;
    return 10000;
  }
  if (d.roots && w.slowUntil > s.tick) return 10000 - d.roots.wave.slowBp;
  return 10000;
};

/** Another order of the hero's own: the stone or the knot it was sent to is off. */
export const druidMarkOff = (s: BattleState): void => {
  const w = s.senda?.druid;
  if (w) w.job = -1;
};

/** How far the goal has come: up the slope (the thaw), the search (the clearing), the roots on the islet (the bridge). */
export const druidGoal = (s: BattleState): { done: number; need: number } => {
  const d = s.rules?.senda?.druid;
  const w = s.senda?.druid;
  if (!d || !w) return { done: 0, need: 1 };
  if (d.thaw) {
    const h = s.heroes[0];
    const span = d.thaw.foot - d.thaw.glade.y;
    const y = h ? imin(d.thaw.foot, imax(d.thaw.glade.y, h.y)) : d.thaw.foot;
    return { done: w.done >= 0 ? span : d.thaw.foot - y, need: span };
  }
  if (d.glade) return { done: imin(s.waveT, d.glade.search), need: d.glade.search };
  const home = w.roots ? w.roots.tips.filter((t) => t.mode === 3).length : 0;
  const braid = w.weave >= 0 && d.roots ? imin(d.roots.weave, s.tick - w.weave) : 0;
  return { done: home * (d.roots?.weave ?? 1) + braid, need: 3 * (d.roots?.weave ?? 1) };
};

/** Won a moment after the goal; the bridge lost out of seeds with nothing growing and no knot to cut. */
export const druidResult = (s: BattleState): 'won' | 'lost' | null => {
  const d = s.rules?.senda?.druid;
  const w = s.senda?.druid;
  if (!d || !w) return null;
  if (w.done >= 0) return s.tick >= w.done + (d.roots ? 36 : 18) ? 'won' : null;
  if (d.roots && w.roots && w.seeds === 0 && w.roots.knot < 0 && !w.roots.tips.some((t) => t.mode === 1) && w.job < 0) return 'lost';
  return null;
};
